import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  FunnelChart, Funnel, LabelList, Sankey, Treemap
} from "recharts";
import {
  TrendingUp, Users, Eye, Clock, Globe, Smartphone, Monitor, Tablet,
  Search, ExternalLink, Activity, Target, Navigation, RefreshCw,
  AlertTriangle, Info, Share2, MousePointer, ChevronRight, ArrowRight,
  MapPin, Zap, BarChart3
} from "lucide-react";
import { format, subDays, parseISO } from "date-fns";
import { DateRange } from "react-day-picker";
import { apiRequest } from "@/lib/queryClient";

// Type definitions for Google Analytics data
interface GAMetrics {
  activeUsers: number;
  sessions: number;
  pageViews: number;
  bounceRate: number;
  avgSessionDuration: string;
  pagesPerSession: number;
  newUsers: number;
}

interface GATrafficSource {
  source: string;
  users: number;
  percentage: number;
}

interface GAPageData {
  pagePath: string;
  pageViews: number;
  percentage: number;
}

interface GACountryData {
  country: string;
  users: number;
  percentage: number;
}

interface GADeviceData {
  deviceCategory: string;
  users: number;
  percentage: number;
}

interface GAUTMData {
  source: string;
  medium: string;
  campaign: string;
  users: number;
  sessions: number;
  conversions: number;
  type: 'paid' | 'organic' | 'referral' | 'direct';
}

interface GAUserJourney {
  pagePath: string;
  nextPagePath: string;
  users: number;
  percentage: number;
}

interface GATimeSeriesData {
  date: string;
  users: number;
  sessions: number;
  pageViews: number;
}

interface GABrowserData {
  browser: string;
  users: number;
  percentage: number;
}

interface GALandingPageData {
  landingPage: string;
  users: number;
  sessions: number;
  bounceRate: number;
}

export default function GoogleAnalyticsDashboard() {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 6), // Last 7 days
    to: new Date(),
  });
  const [activeTab, setActiveTab] = useState("overview");

  // Utility function to convert DateRange to Google Analytics format
  const convertDateRangeToGA = (dateRange: DateRange | undefined) => {
    if (!dateRange?.from || !dateRange?.to) {
      return { startDate: '7daysAgo', endDate: 'today' };
    }
    
    const startDate = format(dateRange.from, 'yyyy-MM-dd');
    const endDate = format(dateRange.to, 'yyyy-MM-dd');
    return { startDate, endDate };
  };

  const { startDate, endDate } = convertDateRangeToGA(dateRange);

  // Google Analytics queries with proper URL construction and authentication
  const { data: gaMetrics, isLoading: gaMetricsLoading } = useQuery<GAMetrics>({
    queryKey: ['google-analytics', 'metrics', startDate, endDate],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/admin/google-analytics/metrics?startDate=${startDate}&endDate=${endDate}`);
      return response.json();
    },
    refetchInterval: 300000, // 5 minutes
  });

  const { data: trafficSources, isLoading: trafficSourcesLoading } = useQuery<GATrafficSource[]>({
    queryKey: ['google-analytics', 'traffic-sources', startDate, endDate],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/admin/google-analytics/traffic-sources?startDate=${startDate}&endDate=${endDate}`);
      return response.json();
    },
    refetchInterval: 300000,
  });

  const { data: topPages, isLoading: topPagesLoading } = useQuery<GAPageData[]>({
    queryKey: ['google-analytics', 'top-pages', startDate, endDate],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/admin/google-analytics/top-pages?startDate=${startDate}&endDate=${endDate}`);
      return response.json();
    },
    refetchInterval: 300000,
  });

  const { data: countries, isLoading: countriesLoading } = useQuery<GACountryData[]>({
    queryKey: ['google-analytics', 'countries', startDate, endDate],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/admin/google-analytics/countries?startDate=${startDate}&endDate=${endDate}`);
      return response.json();
    },
    refetchInterval: 300000,
  });

  const { data: devices, isLoading: devicesLoading } = useQuery<GADeviceData[]>({
    queryKey: ['google-analytics', 'devices', startDate, endDate],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/admin/google-analytics/devices?startDate=${startDate}&endDate=${endDate}`);
      return response.json();
    },
    refetchInterval: 300000,
  });

  const { data: utmData, isLoading: utmLoading } = useQuery<GAUTMData[]>({
    queryKey: ['google-analytics', 'utm-data', startDate, endDate],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/admin/google-analytics/utm-data?startDate=${startDate}&endDate=${endDate}`);
      return response.json();
    },
    refetchInterval: 300000,
  });

  const { data: userJourneys, isLoading: userJourneysLoading } = useQuery<GAUserJourney[]>({
    queryKey: ['google-analytics', 'user-journeys', startDate, endDate],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/admin/google-analytics/user-journeys?startDate=${startDate}&endDate=${endDate}`);
      return response.json();
    },
    refetchInterval: 300000,
  });

  const { data: timeSeriesData, isLoading: timeSeriesLoading } = useQuery<GATimeSeriesData[]>({
    queryKey: ['google-analytics', 'time-series', startDate, endDate],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/admin/google-analytics/time-series?startDate=${startDate}&endDate=${endDate}`);
      return response.json();
    },
    refetchInterval: 300000,
  });

  const { data: browsers, isLoading: browsersLoading } = useQuery<GABrowserData[]>({
    queryKey: ['google-analytics', 'browsers', startDate, endDate],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/admin/google-analytics/browsers?startDate=${startDate}&endDate=${endDate}`);
      return response.json();
    },
    refetchInterval: 300000,
  });

  const { data: landingPages, isLoading: landingPagesLoading } = useQuery<GALandingPageData[]>({
    queryKey: ['google-analytics', 'landing-pages', startDate, endDate],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/admin/google-analytics/landing-pages?startDate=${startDate}&endDate=${endDate}`);
      return response.json();
    },
    refetchInterval: 300000,
  });

  // Color schemes for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];
  const DEVICE_COLORS = { mobile: '#4285F4', desktop: '#34A853', tablet: '#FBBC05', 'smart tv': '#EA4335' };
  const UTM_COLORS = { paid: '#FF6B6B', organic: '#4ECDC4', referral: '#45B7D1', direct: '#96CEB4' };

  // Format time series data for charts
  const formatTimeSeriesData = (data: GATimeSeriesData[] = []) => {
    return data.map(item => ({
      ...item,
      date: format(parseISO(item.date), 'MMM dd'),
    }));
  };

  // Group UTM data by type for pie chart
  const formatUTMDataForPie = (data: GAUTMData[] = []) => {
    const grouped = data.reduce((acc, item) => {
      acc[item.type] = (acc[item.type] || 0) + item.users;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(grouped).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
      color: UTM_COLORS[name as keyof typeof UTM_COLORS] || '#999'
    }));
  };

  // Check if Google Analytics is configured
  const isGAConfigured = import.meta.env.VITE_GA_MEASUREMENT_ID;

  if (!isGAConfigured) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-6 w-6" />
              Google Analytics Dashboard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Google Analytics Not Configured</AlertTitle>
              <AlertDescription>
                Please add your Google Analytics credentials to enable this dashboard.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-8 w-8 text-blue-600" />
            Google Analytics Dashboard
          </h1>
          <p className="text-gray-600 mt-1">Comprehensive analytics with advanced visualizations</p>
        </div>
        <div className="flex items-center gap-4">
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />
          <Badge variant="default" className="bg-green-100 text-green-800">
            Live Data
          </Badge>
        </div>
      </div>

      {/* Quick Metrics Overview */}
      {gaMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Users</p>
                  <p className="text-3xl font-bold text-gray-900" data-testid="metric-active-users">
                    {gaMetrics.activeUsers.toLocaleString()}
                  </p>
                </div>
                <Users className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Sessions</p>
                  <p className="text-3xl font-bold text-gray-900" data-testid="metric-sessions">
                    {gaMetrics.sessions.toLocaleString()}
                  </p>
                </div>
                <Activity className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Page Views</p>
                  <p className="text-3xl font-bold text-gray-900" data-testid="metric-page-views">
                    {gaMetrics.pageViews.toLocaleString()}
                  </p>
                </div>
                <Eye className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Bounce Rate</p>
                  <p className="text-3xl font-bold text-gray-900" data-testid="metric-bounce-rate">
                    {gaMetrics.bounceRate}%
                  </p>
                </div>
                <Target className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Dashboard Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="traffic">Traffic</TabsTrigger>
          <TabsTrigger value="devices">Devices</TabsTrigger>
          <TabsTrigger value="utm">UTM Tracking</TabsTrigger>
          <TabsTrigger value="journey">User Journey</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="technical">Technical</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Time Series Chart */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Traffic Trends
                </CardTitle>
                <CardDescription>Users, sessions, and page views over time</CardDescription>
              </CardHeader>
              <CardContent>
                {timeSeriesLoading ? (
                  <div className="h-80 flex items-center justify-center">
                    <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={formatTimeSeriesData(timeSeriesData)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="users" stroke="#4285F4" strokeWidth={3} name="Users" />
                      <Line type="monotone" dataKey="sessions" stroke="#34A853" strokeWidth={2} name="Sessions" />
                      <Line type="monotone" dataKey="pageViews" stroke="#FBBC05" strokeWidth={2} name="Page Views" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Traffic Sources Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="h-5 w-5" />
                  Traffic Sources
                </CardTitle>
                <CardDescription>Where your users come from</CardDescription>
              </CardHeader>
              <CardContent>
                {trafficSourcesLoading ? (
                  <div className="h-64 flex items-center justify-center">
                    <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={trafficSources}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percentage }) => `${name}: ${percentage}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="users"
                      >
                        {trafficSources?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Geographic Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Top Countries
                </CardTitle>
                <CardDescription>User distribution by country</CardDescription>
              </CardHeader>
              <CardContent>
                {countriesLoading ? (
                  <div className="h-64 flex items-center justify-center">
                    <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={countries} layout="horizontal">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="country" type="category" width={100} />
                      <RechartsTooltip />
                      <Bar dataKey="users" fill="#4285F4">
                        <LabelList dataKey="percentage" position="right" formatter={(value: number) => `${value}%`} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Traffic Tab */}
        <TabsContent value="traffic" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Traffic Sources Detail */}
            <Card>
              <CardHeader>
                <CardTitle>Traffic Sources Breakdown</CardTitle>
                <CardDescription>Detailed view of traffic channels</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {trafficSources?.map((source, index) => (
                    <div key={source.source} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="font-medium">{source.source}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{source.users.toLocaleString()}</div>
                        <div className="text-sm text-gray-500">{source.percentage}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Landing Pages Performance */}
            <Card>
              <CardHeader>
                <CardTitle>Top Landing Pages</CardTitle>
                <CardDescription>Pages where users enter your site</CardDescription>
              </CardHeader>
              <CardContent>
                {landingPagesLoading ? (
                  <div className="h-64 flex items-center justify-center">
                    <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={landingPages}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="landingPage" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <RechartsTooltip />
                      <Bar dataKey="users" fill="#34A853" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Devices Tab */}
        <TabsContent value="devices" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Device Categories Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="h-5 w-5" />
                  Device Categories
                </CardTitle>
                <CardDescription>User device distribution</CardDescription>
              </CardHeader>
              <CardContent>
                {devicesLoading ? (
                  <div className="h-64 flex items-center justify-center">
                    <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={devices?.map(device => ({
                          ...device,
                          fill: DEVICE_COLORS[device.deviceCategory as keyof typeof DEVICE_COLORS] || '#999'
                        }))}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="users"
                        label={({ deviceCategory, percentage }) => `${deviceCategory}: ${percentage}%`}
                      >
                        {devices?.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={DEVICE_COLORS[entry.deviceCategory as keyof typeof DEVICE_COLORS] || '#999'} 
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Device Statistics */}
            <Card>
              <CardHeader>
                <CardTitle>Device Statistics</CardTitle>
                <CardDescription>Detailed device usage metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {devices?.map((device) => (
                    <div key={device.deviceCategory} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {device.deviceCategory === 'mobile' && <Smartphone className="h-5 w-5 text-blue-600" />}
                        {device.deviceCategory === 'desktop' && <Monitor className="h-5 w-5 text-green-600" />}
                        {device.deviceCategory === 'tablet' && <Tablet className="h-5 w-5 text-orange-600" />}
                        {device.deviceCategory === 'smart tv' && <Monitor className="h-5 w-5 text-purple-600" />}
                        <div>
                          <p className="font-medium capitalize">{device.deviceCategory}</p>
                          <p className="text-sm text-gray-500">{device.percentage}% of users</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">{device.users.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">users</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Browser Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Browser Distribution</CardTitle>
              <CardDescription>What browsers your users prefer</CardDescription>
            </CardHeader>
            <CardContent>
              {browsersLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={browsers}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="browser" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="users" fill="#FF6B6B">
                      <LabelList dataKey="percentage" position="top" formatter={(value: number) => `${value}%`} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* UTM Tracking Tab */}
        <TabsContent value="utm" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* UTM Type Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Campaign Type Distribution
                </CardTitle>
                <CardDescription>Paid vs Organic vs Direct traffic</CardDescription>
              </CardHeader>
              <CardContent>
                {utmLoading ? (
                  <div className="h-64 flex items-center justify-center">
                    <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={formatUTMDataForPie(utmData)}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {formatUTMDataForPie(utmData).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* UTM Campaign Details */}
            <Card>
              <CardHeader>
                <CardTitle>Campaign Performance</CardTitle>
                <CardDescription>Top performing campaigns</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {utmData?.slice(0, 10).map((campaign, index) => (
                    <div key={`${campaign.source}-${campaign.medium}-${index}`} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant="outline" 
                            style={{ 
                              backgroundColor: UTM_COLORS[campaign.type], 
                              color: 'white',
                              borderColor: UTM_COLORS[campaign.type]
                            }}
                          >
                            {campaign.type}
                          </Badge>
                          <span className="font-medium">{campaign.source}</span>
                        </div>
                        <span className="text-lg font-bold">{campaign.users.toLocaleString()}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>Medium: {campaign.medium}</p>
                        <p>Campaign: {campaign.campaign}</p>
                        <p>Sessions: {campaign.sessions.toLocaleString()} | Conversions: {campaign.conversions}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* User Journey Tab */}
        <TabsContent value="journey" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Navigation className="h-5 w-5" />
                User Journey Flow
              </CardTitle>
              <CardDescription>How users navigate through your website</CardDescription>
            </CardHeader>
            <CardContent>
              {userJourneysLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {userJourneys?.slice(0, 8).map((journey, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="bg-blue-100 p-2 rounded-full">
                          <ExternalLink className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">{journey.pagePath}</p>
                          <p className="text-sm text-gray-600">Entry Point</p>
                        </div>
                      </div>
                      
                      <ArrowRight className="h-5 w-5 text-gray-400" />
                      
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-medium">{journey.nextPagePath}</p>
                          <p className="text-sm text-gray-600">Next Page</p>
                        </div>
                        <div className="bg-green-100 p-2 rounded-full">
                          <Users className="h-4 w-4 text-green-600" />
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-lg font-bold">{journey.users.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">{journey.percentage}% of users</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Content Tab */}
        <TabsContent value="content" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Top Performing Pages
              </CardTitle>
              <CardDescription>Most viewed pages on your website</CardDescription>
            </CardHeader>
            <CardContent>
              {topPagesLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={topPages} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="pagePath" 
                      angle={-45} 
                      textAnchor="end" 
                      height={120}
                      interval={0}
                    />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="pageViews" fill="#4285F4">
                      <LabelList dataKey="percentage" position="top" formatter={(value: number) => `${value}%`} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Technical Tab */}
        <TabsContent value="technical" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Technical Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Technical Metrics
                </CardTitle>
                <CardDescription>Site performance indicators</CardDescription>
              </CardHeader>
              <CardContent>
                {gaMetrics && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">Average Session Duration</p>
                        <p className="text-sm text-gray-600">Time users spend on site</p>
                      </div>
                      <p className="text-2xl font-bold">{gaMetrics.avgSessionDuration}</p>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">Pages Per Session</p>
                        <p className="text-sm text-gray-600">Pages viewed per visit</p>
                      </div>
                      <p className="text-2xl font-bold">{gaMetrics.pagesPerSession.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">New vs Returning</p>
                        <p className="text-sm text-gray-600">User acquisition</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold">{gaMetrics.newUsers.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">new users</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Browser Performance */}
            <Card>
              <CardHeader>
                <CardTitle>Browser Performance</CardTitle>
                <CardDescription>User preferences by browser</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {browsers?.map((browser, index) => (
                    <div key={browser.browser} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="font-medium">{browser.browser}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{browser.users.toLocaleString()}</div>
                        <div className="text-sm text-gray-500">{browser.percentage}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Data Source Info */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Data Source</AlertTitle>
        <AlertDescription>
          All data is fetched in real-time from Google Analytics 4. Data updates every 5 minutes automatically.
          Use the date range picker to view different time periods.
        </AlertDescription>
      </Alert>
    </div>
  );
}